import { OTelSpan, SpanEvent, SpanStatus } from "./types";

function safeJsonParse(s: string, fallback: any = {}): any {
  if (!s) return fallback;
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
}

export function parseRawSpan(raw: any): OTelSpan {
  const startNs = BigInt(raw.start_time_ns || "0");
  const endNs = BigInt(raw.end_time_ns || "0");
  const durationNs = endNs > startNs ? endNs - startNs : BigInt(0);
  const durationMs = Number(durationNs / BigInt(1_000_000));

  const attributes = safeJsonParse(raw.attributes, {});
  const events = (safeJsonParse(raw.events, []) as any[]).map((e) => ({
    name: String(e.name ?? ""),
    timestamp: String(e.timestamp ?? ""),
    attributes: e.attributes ?? {},
  }));

  const status: SpanStatus = {
    code: (raw.status_code as "OK" | "ERROR" | "UNSET") || "UNSET",
    description: raw.status_message,
  };

  return {
    id: raw.span_id,
    traceId: raw.trace_id,
    parentId: raw.parent_span_id || undefined,
    name: raw.name,
    kind: raw.kind,
    startTime: new Date(Number(startNs / BigInt(1_000_000))).toISOString(),
    endTime: new Date(Number(endNs / BigInt(1_000_000))).toISOString(),
    durationMs,
    attributes,
    events,
    status,
  };
}

export function buildSpanTree(rawSpans: any[]): OTelSpan[] {
  const parsed = rawSpans.map(parseRawSpan);
  if (parsed.length === 0) return [];

  const byId = new Map<string, OTelSpan>();
  for (const span of parsed) {
    span.children = [];
    byId.set(span.id, span);
  }

  const roots: OTelSpan[] = [];
  for (const span of parsed) {
    if (span.parentId && byId.has(span.parentId)) {
      byId.get(span.parentId)!.children!.push(span);
    } else {
      roots.push(span);
    }
  }

  // Sort children by start time
  function sortChildren(span: OTelSpan) {
    if (span.children) {
      span.children.sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
      for (const child of span.children) {
        sortChildren(child);
      }
    }
  }

  roots.sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );
  for (const root of roots) {
    sortChildren(root);
  }

  return roots;
}

export interface TraceGroup {
  traceId: string;
  startTime: string;
  roots: OTelSpan[];
  nodeId?: string;
  nodeIteration?: number;
}

/** Partition spans into per-trace groups (one trace per agent turn / node
 * execution / side task), each with its own span tree, ordered
 * chronologically. Node identity (name, iteration) comes from the noxus.*
 * attributes stamped on every span. */
export function buildTraceGroups(rawSpans: any[]): TraceGroup[] {
  const roots = buildSpanTree(rawSpans);
  const groups = new Map<string, TraceGroup>();
  for (const root of roots) {
    const existing = groups.get(root.traceId);
    if (existing) {
      existing.roots.push(root);
      if (root.startTime < existing.startTime) {
        existing.startTime = root.startTime;
      }
    } else {
      const nodeId = root.attributes["noxus.node.id"];
      const nodeIteration = root.attributes["noxus.node.iteration"];
      groups.set(root.traceId, {
        traceId: root.traceId,
        startTime: root.startTime,
        roots: [root],
        nodeId: typeof nodeId === "string" ? nodeId : undefined,
        nodeIteration:
          typeof nodeIteration === "number" ? nodeIteration : undefined,
      });
    }
  }
  return [...groups.values()].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );
}
